<script setup lang="ts">
type SuccessProbabilityMode = 'negotiationBuyer' | 'negotiationSeller' | 'priceReduction';

// PROPS
interface Props {
    selectedAmount: number;
    referenceAmount: number;
    guaranteedAmount?: number;
    mode: SuccessProbabilityMode;
}

const { selectedAmount, referenceAmount, guaranteedAmount, mode } = defineProps<Props>();

// COMPOSABLES
const { tt } = useTypedI18n();

// DATA
const successProbability = computed(() => {
    if (guaranteedAmount !== undefined && selectedAmount === guaranteedAmount) return 100;

    switch (mode) {
        case 'negotiationBuyer':
            return getBuyerSideAcceptanceProbability(selectedAmount, referenceAmount);
        case 'negotiationSeller':
            return getSellerSideAcceptanceProbability(selectedAmount, referenceAmount);
        case 'priceReduction':
            return getPriceReductionSuccessProbability(selectedAmount, referenceAmount);
    }
});

type ProbabilityLevel = 'guaranteed' | 'veryHigh' | 'high' | 'mediumHigh' | 'medium' | 'mediumLow' | 'low';

const probabilityLevel = computed<ProbabilityLevel>(() => {
    const p = successProbability.value;

    if (p >= 100) return 'guaranteed';

    if (mode === 'negotiationBuyer') {
        if (p >= 94) return 'veryHigh';
        if (p >= 80) return 'high';
        if (p >= 71) return 'mediumHigh';
        if (p >= 66) return 'medium';
        if (p >= 60) return 'mediumLow';
    } else if (mode === 'negotiationSeller') {
        if (p >= 90) return 'veryHigh';
        if (p >= 76) return 'high';
        if (p >= 64) return 'mediumHigh';
        if (p >= 57) return 'medium';
        if (p >= 50) return 'mediumLow';
    } else if (mode === 'priceReduction') {
        if (p >= 41) return 'veryHigh';
        if (p >= 30) return 'high';
        if (p >= 22) return 'mediumHigh';
        if (p >= 13) return 'medium';

        return 'mediumLow';
    }

    return 'low';
});

const i18nSubNamespace = computed(() => (mode === 'priceReduction' ? 'priceReduction' : 'negotiation'));

const title = computed(() =>
    tt(`sale.successProbability.${i18nSubNamespace.value}.${probabilityLevel.value}`, {
        probability: Math.round(successProbability.value),
    })
);

const barColor = computed<string>(() => {
    const map: Record<ProbabilityLevel, string> = {
        guaranteed: 'bg-green-700',
        veryHigh: 'bg-green-600',
        high: 'bg-green-400',
        mediumHigh: 'bg-lime-400',
        medium: 'bg-yellow-400',
        mediumLow: 'bg-amber-500',
        low: 'bg-red-500',
    };

    return map[probabilityLevel.value];
});

// METHODS
/**
 * Buyer-side: piecewise linear approximation of the offer acceptance probability.
 * Input: buyer's offer amount vs. seller's reference request.
 * Higher ratio (offer / request) → higher probability.
 */
function getBuyerSideAcceptanceProbability(selectedOffer: number, referenceRequest: number): number {
    if (referenceRequest <= 0) {
        return 0;
    }

    if (selectedOffer >= referenceRequest) {
        return 100;
    }

    const ratio = selectedOffer / referenceRequest;

    let probability: number;

    if (ratio >= 0.95) {
        probability = 120.43 * ratio - 20.34;
    } else if (ratio >= 0.89) {
        probability = 229.26 * ratio - 123.5;
    } else if (ratio >= 0.62) {
        probability = 107.36 * ratio - 15.64;
    } else if (ratio >= 0.48) {
        probability = 172.81 * ratio - 56.44;
    } else {
        probability = 215.39 * ratio - 77.09;
    }

    return Math.min(100, Math.max(1, probability));
}

/**
 * Seller-side: piecewise linear approximation of the request acceptance probability.
 * Input: seller's request amount vs. the buyer's reference offer.
 * Higher ratio (request / offer) → lower probability.
 */
function getSellerSideAcceptanceProbability(selectedRequest: number, referenceOffer: number): number {
    if (referenceOffer <= 0) {
        return 0;
    }

    if (selectedRequest <= referenceOffer) {
        return 100;
    }

    const ratio = selectedRequest / referenceOffer;

    let probability: number;

    if (ratio <= 1.02) {
        probability = 210 - 110 * ratio;
    } else if (ratio <= 1.05) {
        probability = 426.92 - 322.67 * ratio;
    } else if (ratio <= 1.09) {
        probability = 493.95 - 386.5 * ratio;
    } else if (ratio <= 1.15) {
        probability = 479.05 - 372.83 * ratio;
    } else {
        probability = 932.57 - 767.2 * ratio;
    }

    return Math.min(100, Math.max(1, probability));
}

/**
 * Price reduction: piecewise linear approximation of the sale success probability.
 * Input: reduced price vs. original (first) price.
 * Lower ratio (bigger reduction) → higher probability, capped at 90.
 */
function getPriceReductionSuccessProbability(reducedPrice: number, initialPrice: number): number {
    if (initialPrice <= 0) {
        return 0;
    }

    if (reducedPrice >= initialPrice) {
        return 1;
    }

    const ratio = reducedPrice / initialPrice;

    let probability: number;

    if (ratio >= 0.77) {
        probability = 218.09 * (1 - ratio);
    } else {
        probability = -132.34 * ratio + 151.82;
    }

    return Math.min(90, Math.max(1, probability));
}
</script>

<template>
    <div class="pb-4">
        <p class="text-highlighted text-sm font-medium">{{ title }}</p>
        <UProgress class="mt-2" :model-value="successProbability" :ui="{ indicator: barColor }" />
    </div>
</template>
